import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Button, Form, Row, Col, Table, Badge,
    Spinner, Modal, Alert
} from 'react-bootstrap';
import { Play, CheckCircle, Download, Mail, History, FileText } from 'lucide-react';
import axiosInstance from '../../api/axiosConfig';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import PayrollAnalytics from './PayrollAnalytics';
import PayrollFinanceApproval from './PayrollFinanceApproval';
// ── Confirmation Modal ────────────────────────────────────────────────────────
const ConfirmModal = ({ show, onHide, onConfirm, title, message, confirmLabel, loading }) => (
    <Modal show={show} onHide={onHide} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-3">
            <p className="mb-0 text-muted small">{message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onHide} disabled={loading}>Cancel</Button>
            <Button variant="primary" onClick={onConfirm} disabled={loading}>
                {loading && <Spinner size="sm" className="me-1"/>}{confirmLabel}
            </Button>
        </Modal.Footer>
    </Modal>
);
// ── Financial year helper ─────────────────────────────────────────────────────
const getFinancialYears = () => {
    const now = new Date();
    const currentFYStart = now.getMonth() >= 3
        ? now.getFullYear()
        : now.getFullYear() - 1;
    return [0, 1, 2].map(offset => {
        const y = currentFYStart - offset;
        return `${y}-${String(y + 1).slice(2)}`;
    });
};
// ── Shared download helper — forces browser to download instead of opening in tab ──
const forceDownload = (data, filename) => {
    const blob = new Blob([data], { type: 'application/octet-stream' });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href         = url;
    link.download     = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, 150);
};
// ── Form 16 Tab ───────────────────────────────────────────────────────────────
const Form16Tab = () => {
    const financialYears = getFinancialYears();
    const [fy,          setFy]          = useState(financialYears[0]);
    const [loading,     setLoading]     = useState(false);
    const [employees,   setEmployees]   = useState([]);
    const [downloading, setDownloading] = useState({});
    const [searched,    setSearched]    = useState(false);
    const fetchList = async () => {
        setLoading(true);
        setSearched(true);
        try {
            const res = await axiosInstance.get(`/payroll/form16/list?financial_year=${fy}`);
            setEmployees(res.data.data?.employees || []);
            if (!res.data.data?.employees?.length) {
                toast.info(`No payroll records found for FY ${fy}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to fetch Form 16 list');
        } finally { setLoading(false); }
    };
    const downloadForm16 = async (empId, empName) => {
        setDownloading(prev => ({ ...prev, [empId]: true }));
        try {
            const res = await axiosInstance.get(
                `/payroll/form16/${empId}?financial_year=${fy}`,
                { responseType: 'blob' }
            );
            const contentType = res.headers['content-type'] || '';
            const ext  = contentType.includes('pdf') ? 'pdf' : 'html';
            const name = `Form16_${fy.replace('-', '_')}_${empName.replace(/\s/g, '_')}.${ext}`;
            forceDownload(res.data, name);
            toast.success(`Form 16 downloaded for ${empName}`);
        } catch (err) {
            const msg = err.response?.data?.message || 'Download failed';
            toast.error(msg);
        } finally {
            setDownloading(prev => ({ ...prev, [empId]: false }));
        }
    };
    const cur = (v) => new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(parseFloat(v || 0));
    return (
        <div>
            {/* FY Selector */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="align-items-end g-3">
                        <Col xs={12} md={4}>
                            <Form.Label className="small fw-bold">Financial Year</Form.Label>
                            <Form.Select value={fy} onChange={e => { setFy(e.target.value); setEmployees([]); setSearched(false); }}>
                                {financialYears.map(y => (
                                    <option key={y} value={y}>FY {y}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs="auto">
                            <Button variant="primary" onClick={fetchList} disabled={loading}
                                className="d-flex align-items-center gap-2">
                                {loading ? <Spinner size="sm"/> : <FileText size={18}/>}
                                Load Form 16 List
                            </Button>
                        </Col>
                    </Row>
                    <div className="mt-3 p-3 rounded-3 bg-light small text-muted">
                        <strong>📋 About Form 16:</strong> Form 16 is the TDS certificate issued by the employer.
                        Part A shows quarterly TDS deducted. Part B shows salary breakup and taxable income.
                        Employees need this to file their Income Tax Return (ITR).
                    </div>
                </Card.Body>
            </Card>
            {/* Employee List */}
            {searched && !loading && employees.length === 0 && (
                <Alert variant="info">
                    No payroll records found for FY {fy}.
                    Run payroll for at least one month in this financial year first.
                </Alert>
            )}
            {employees.length > 0 && (
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-0 py-3">
                        <div className="d-flex align-items-center justify-content-between">
                            <span className="fw-bold">
                                Form 16 — FY {fy}
                                <Badge bg="secondary" className="ms-2">{employees.length} employees</Badge>
                            </span>
                            <span className="text-muted small">
                                Click Download to generate individual Form 16 PDF
                            </span>
                        </div>
                    </Card.Header>
                    <Table hover responsive className="mb-0 align-middle small">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                <th className="ps-4">Employee</th>
                                <th>PAN</th>
                                <th className="text-end">Gross Salary</th>
                                <th className="text-end">Total TDS</th>
                                <th className="text-end">Net Pay</th>
                                <th className="text-end">Months</th>
                                <th className="text-end pe-4">Form 16</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees.map(emp => (
                                <tr key={emp.id}>
                                    <td className="ps-4">
                                        <div className="fw-bold">{emp.first_name} {emp.last_name}</div>
                                        <div className="text-muted" style={{ fontSize: 11 }}>
                                            {emp.employee_code || '—'} {emp.designation_name ? `| ${emp.designation_name}` : ''}
                                        </div>
                                    </td>
                                    <td>
                                        {emp.pan_number
                                            ? <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{emp.pan_number}</span>
                                            : <Badge bg="danger" style={{ fontSize: 10 }}>NOT SET</Badge>
                                        }
                                    </td>
                                    <td className="text-end fw-bold">{cur(emp.total_gross)}</td>
                                    <td className="text-end text-danger fw-bold">{cur(emp.total_tds)}</td>
                                    <td className="text-end text-success fw-bold">{cur(emp.total_net)}</td>
                                    <td className="text-end">{emp.payslip_count}</td>
                                    <td className="text-end pe-4">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            disabled={!!downloading[emp.id]}
                                            onClick={() => downloadForm16(emp.id, `${emp.first_name} ${emp.last_name}`)}
                                            className="d-inline-flex align-items-center gap-1">
                                            {downloading[emp.id]
                                                ? <Spinner size="sm"/>
                                                : <Download size={14}/>
                                            }
                                            <span className="d-none d-sm-inline">Form 16</span>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-light">
                            <tr className="fw-bold">
                                <td className="ps-4" colSpan={2}>Total ({employees.length} employees)</td>
                                <td className="text-end">
                                    {cur(employees.reduce((s, e) => s + parseFloat(e.total_gross || 0), 0))}
                                </td>
                                <td className="text-end text-danger">
                                    {cur(employees.reduce((s, e) => s + parseFloat(e.total_tds || 0), 0))}
                                </td>
                                <td className="text-end text-success">
                                    {cur(employees.reduce((s, e) => s + parseFloat(e.total_net || 0), 0))}
                                </td>
                                <td colSpan={2}></td>
                            </tr>
                        </tfoot>
                    </Table>
                    <Card.Footer className="bg-white border-0 text-muted small py-3 text-center">
                        💡 Tip: Ensure PAN numbers are entered in Employee Profile before distributing Form 16.
                        Form 16 without PAN is not valid for ITR filing.
                    </Card.Footer>
                </Card>
            )}
        </div>
    );
};
// ── Main Component ────────────────────────────────────────────────────────────
const PayrollManagement = () => {
    const { user }    = useAuth();
    const [month,     setMonth]         = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [processing,     setProcessing]     = useState(false);
    const [emailing,       setEmailing]       = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [results,        setResults]        = useState([]);
    const [analytics,      setAnalytics]      = useState(null);
    const [runModal,       setRunModal]       = useState(false);
    const [emailModal,     setEmailModal]     = useState(false);
    const [activeTab,      setActiveTab]      = useState('payroll');
    const [filterSearch,   setFilterSearch]   = useState('');
    const [filterDept,     setFilterDept]     = useState('');
    const [pageNum,        setPageNum]        = useState(1);
    const [emailResult,    setEmailResult]    = useState(null);
    const PAGE_SIZE = 15;
    const fetchAnalytics = useCallback(async (m) => {
        try {
            const res = await axiosInstance.get(`/payroll/analytics?month_year=${m}`);
            setAnalytics(res.data.data || null);
        } catch { /* silent */ }
    }, []);
    const [runStatus,  setRunStatus]  = useState(null);
    const [runDetails, setRunDetails] = useState(null);
    const fetchPayrollHistory = useCallback(async (silent = false) => {
        setLoadingHistory(true);
        setEmailResult(null);
        try {
            const [histRes, runsRes] = await Promise.allSettled([
                axiosInstance.get(`/payroll/history?month_year=${month}`),
                axiosInstance.get(`/payroll/runs?month_year=${month}`),
            ]);
            const data = histRes.status === 'fulfilled' ? (histRes.value.data.data || []) : [];
            setResults(data);
            setPageNum(1);
            setFilterSearch('');
            setFilterDept('');
            if (runsRes.status === 'fulfilled') {
                const run = (runsRes.value.data.data || [])[0];
                setRunStatus(run?.status || null);
                setRunDetails(run || null);
                // FIX: only show these toasts on explicit user action (View History / Run),
                // not on the silent auto-fetch triggered by changing the month dropdown.
                if (!silent) {
                    if (run?.status === 'rejected') {
                        toast.warn('Payroll run was rejected by finance.');
                    } else if (run?.status === 'pending_approval') {
                        toast.info(`Payroll for ${month} is pending finance approval.`);
                    } else if (data.length === 0) {
                        toast.info('No records for this month. Run the engine to generate.');
                    } else {
                        toast.success(`Loaded ${data.length} payroll records.`);
                    }
                }
            } else {
                setRunStatus(null);
                setRunDetails(null);
                if (!silent) {
                    if (data.length === 0) toast.info('No records for this month.');
                    else toast.success(`Loaded ${data.length} payroll records.`);
                }
            }
            await fetchAnalytics(month);
        } catch {
            if (!silent) toast.error('Failed to fetch payroll history.');
        } finally { setLoadingHistory(false); }
    }, [month, fetchAnalytics]);
    // FIX: re-fetch payroll history + run status whenever the selected month
    // changes, so runStatus is always accurate for the CURRENTLY selected
    // month before the user can click Run Payroll. Previously runStatus
    // stayed stale from whichever month was last fetched, which meant the
    // Run Payroll button could be wrongly enabled for a month that already
    // had a pending/processed run.
    useEffect(() => {
        fetchPayrollHistory(true); // silent = true, no toast spam on every month change
    }, [month, fetchPayrollHistory]);
    const handleRunPayroll = async () => {
        setRunModal(false);
        setProcessing(true);
        setEmailResult(null);
        try {
            const [y, m] = month.split('-');
            const res = await axiosInstance.post('/payroll/run', {
                month: parseInt(m), year: parseInt(y),
            });
            await fetchPayrollHistory();
            toast.success(res.data.message || 'Payroll processed successfully.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Processing failed.');
            // Refresh status even on failure — e.g. if the backend rejected
            // because a run is already pending_approval, we want the button
            // to reflect that immediately instead of staying clickable.
            await fetchPayrollHistory(true);
        } finally { setProcessing(false); }
    };
    const handleBulkEmail = async () => {
        setEmailModal(false);
        setEmailing(true);
        setEmailResult(null);
        try {
            const res = await axiosInstance.post('/payroll/bulk-email', { month_year: month });
            const data = res.data.data || {};
            setEmailResult(data);
            toast.success(
                `✅ Payslips sent: ${data.sent} delivered, ${data.failed} failed, ${data.skipped} skipped`
            );
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to send emails.';
            toast.error(msg);
            setEmailResult({ error: msg });
        } finally { setEmailing(false); }
    };
    const downloadPayslip = async (payslipId, empName) => {
        try {
            const res = await axiosInstance.get(
                `/payroll/download/${payslipId}`,
                { responseType: 'blob' }
            );
            const contentType = res.headers['content-type'] || '';
            const ext  = contentType.includes('pdf') ? 'pdf' : 'html';
            const name = `Payslip_${empName?.replace(/\s/g, '_') || payslipId}_${month}.${ext}`;
            forceDownload(res.data, name);
        } catch {
            toast.error('Download failed. Payslip may not be generated yet.');
        }
    };
    const cur = (v) => new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(parseFloat(v || 0));
    const filteredResults = results.filter(rec => {
        const name = rec.first_name ? `${rec.first_name} ${rec.last_name}` : (rec.name || '');
        const code = rec.employee_code || '';
        const matchSearch = !filterSearch ||
            name.toLowerCase().includes(filterSearch.toLowerCase()) ||
            code.toLowerCase().includes(filterSearch.toLowerCase());
        const matchDept = !filterDept || rec.department_name === filterDept;
        return matchSearch && matchDept;
    });
    const totalPages       = Math.ceil(filteredResults.length / PAGE_SIZE);
    const paginatedResults = filteredResults.slice((pageNum - 1) * PAGE_SIZE, pageNum * PAGE_SIZE);
    // FIX: Run Payroll button logic, two layers:
    //   1. pending_approval / processing -> ALWAYS blocked (must resolve first)
    //   2. processed / approved -> blocked ONLY if the selected month is a
    //      PAST month. If it's the CURRENT month, re-running is allowed but
    //      shows a stronger warning (payslips may have already been viewed
    //      or downloaded by employees).
    // This mirrors the backend guard in payroll.controller.js exactly —
    // the backend is the authoritative check; this just keeps the UI honest.
    const isCurrentMonth = (() => {
        const now = new Date();
        const nowStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        return month === nowStr;
    })();
    const isClosedPastMonth = ['processed', 'approved'].includes(runStatus) && !isCurrentMonth;
    const isReRunCurrentMonth = ['processed', 'approved'].includes(runStatus) && isCurrentMonth;
    const runBlocked  = runStatus === 'pending_approval' || runStatus === 'processing' || isClosedPastMonth;
    const runDisabled = processing || runBlocked;
    const runButtonLabel = runStatus === 'pending_approval' ? 'Pending Finance Approval'
        : runStatus === 'processing'   ? 'Processing…'
        : isClosedPastMonth            ? 'Closed (Past Month)'
        : isReRunCurrentMonth          ? 'Re-run Payroll'
        : 'Run Payroll';
    return (
        <div className="p-2 p-md-4">
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                <h4 className="fw-bold mb-0">Payroll Engine</h4>
                <div className="d-flex gap-2 flex-wrap">
                    <Button size="sm"
                        variant={activeTab === 'payroll' ? 'primary' : 'outline-secondary'}
                        onClick={() => setActiveTab('payroll')}>
                        ⚙ Payroll Run
                    </Button>
                    <Button size="sm"
                        variant={activeTab === 'form16' ? 'primary' : 'outline-secondary'}
                        onClick={() => setActiveTab('form16')}>
                        📄 Form 16
                    </Button>
                    <Button size="sm"
                        variant={activeTab === 'finance' ? 'primary' : 'outline-secondary'}
                        onClick={() => setActiveTab('finance')}>
                        💰 Finance Approval
                    </Button>
                </div>
            </div>
            {activeTab === 'finance' && <PayrollFinanceApproval />}
            {activeTab === 'form16'  && <Form16Tab />}
            {activeTab === 'payroll' && (
                <>
                    <PayrollAnalytics stats={analytics}/>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="align-items-end g-3">
                                <Col xs={12} md={4}>
                                    <Form.Label className="small fw-bold">Processing Month</Form.Label>
                                    <Form.Control
                                        type="month"
                                        value={month}
                                        onChange={e => setMonth(e.target.value)}
                                    />
                                </Col>
                                <Col xs="auto">
                                    <Button variant={runBlocked ? 'outline-warning' : 'primary'}
                                        onClick={() => setRunModal(true)}
                                        disabled={runDisabled}
                                        title={runBlocked
                                            ? 'A payroll run already exists for this month. Resolve it in the Finance Approval tab before running again.'
                                            : undefined}
                                        className="d-flex align-items-center gap-2">
                                        {processing ? <Spinner size="sm"/> : <Play size={18}/>}
                                        {runButtonLabel}
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <Button variant="outline-secondary" onClick={() => fetchPayrollHistory(false)}
                                        disabled={loadingHistory || processing}
                                        className="d-flex align-items-center gap-2">
                                        {loadingHistory ? <Spinner size="sm"/> : <History size={18}/>}
                                        View History
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setEmailModal(true)}
                                        disabled={emailing || results.length === 0}
                                        className="d-flex align-items-center gap-2">
                                        {emailing ? <Spinner size="sm"/> : <Mail size={18}/>}
                                        Email All Payslips
                                    </Button>
                                </Col>
                            </Row>
                            {runBlocked && (
                                <div className="mt-3 small text-warning d-flex align-items-center gap-2">
                                    <CheckCircle size={14}/>
                                    {runStatus === 'pending_approval'
                                        ? 'This month already has a payroll run waiting for finance approval. Go to the Finance Approval tab to resolve it before running again.'
                                        : runStatus === 'processing'
                                        ? 'This month is currently being processed. Please wait and refresh.'
                                        : 'This month was already processed and released, and is now a past month. Past months are locked to protect completed bank transfers and tax filings.'}
                                </div>
                            )}
                            {isReRunCurrentMonth && !runBlocked && (
                                <div className="mt-3 small text-danger d-flex align-items-center gap-2">
                                    <CheckCircle size={14}/>
                                    This month was already processed and released. Re-running will recalculate
                                    and overwrite the existing payslips — employees may have already viewed
                                    or downloaded them.
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                    {emailResult && !emailResult.error && (
                        <Alert
                            variant={emailResult.failed > 0 ? 'warning' : 'success'}
                            dismissible
                            onClose={() => setEmailResult(null)}
                            className="mb-4">
                            <Alert.Heading className="h6">
                                📧 Bulk Email Result — {emailResult.month} {emailResult.year}
                            </Alert.Heading>
                            <div className="d-flex gap-4 flex-wrap mt-2">
                                <span><strong className="text-success">✅ Sent:</strong> {emailResult.sent}</span>
                                <span><strong className="text-danger">❌ Failed:</strong> {emailResult.failed}</span>
                                <span>
                                    <strong className="text-muted">⏭ Skipped:</strong> {emailResult.skipped}
                                    {emailResult.skipped > 0 && <small className="ms-1 text-muted">(no email on file)</small>}
                                </span>
                                <span><strong>Total:</strong> {emailResult.total}</span>
                            </div>
                            {emailResult.errors?.length > 0 && (
                                <div className="mt-2 small">
                                    <strong>Failed deliveries:</strong>
                                    <ul className="mb-0 mt-1">
                                        {emailResult.errors.slice(0, 5).map((e, i) => (
                                            <li key={i}>{e.employee} ({e.email}) — {e.error}</li>
                                        ))}
                                        {emailResult.errors.length > 5 && (
                                            <li>...and {emailResult.errors.length - 5} more</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </Alert>
                    )}
                    {runDetails && runStatus && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                                    <h6 className="fw-bold mb-0">Payroll Run Details — {month}</h6>
                                    <Badge
                                        bg={
                                            runStatus === 'processed' || runStatus === 'approved' ? 'success' :
                                            runStatus === 'pending_approval' ? 'warning' :
                                            runStatus === 'rejected' ? 'danger' : 'secondary'
                                        }
                                        text={runStatus === 'pending_approval' ? 'dark' : 'white'}
                                        style={{ fontSize: 13, padding: '6px 14px' }}>
                                        {runStatus === 'processed'        ? '✅ Processed & Released' :
                                         runStatus === 'approved'         ? '✅ Approved' :
                                         runStatus === 'pending_approval' ? '⏳ Pending Finance Approval' :
                                         runStatus === 'rejected'         ? '❌ Rejected by Finance' :
                                         runStatus}
                                    </Badge>
                                </div>
                                <Row className="g-3">
                                    {runDetails.processed_by_name && (
                                        <Col xs={12} sm={6} md={3}>
                                            <div className="small text-muted fw-bold mb-1">Processed By</div>
                                            <div className="fw-medium">{runDetails.processed_by_name}</div>
                                        </Col>
                                    )}
                                    {runDetails.approved_by_name && (
                                        <Col xs={12} sm={6} md={3}>
                                            <div className="small text-muted fw-bold mb-1">Approved By</div>
                                            <div className="fw-medium text-success">{runDetails.approved_by_name}</div>
                                        </Col>
                                    )}
                                    {runDetails.rejected_by_name && (
                                        <Col xs={12} sm={6} md={3}>
                                            <div className="small text-muted fw-bold mb-1">Rejected By</div>
                                            <div className="fw-medium text-danger">{runDetails.rejected_by_name}</div>
                                        </Col>
                                    )}
                                </Row>
                                {runStatus === 'rejected' && runDetails.reject_reason && (
                                    <div className="mt-3 p-3 rounded-3 border border-danger-subtle"
                                        style={{ background: '#fff5f5' }}>
                                        <div className="small fw-bold text-danger mb-1">❌ Rejection Reason</div>
                                        <div className="small">{runDetails.reject_reason}</div>
                                    </div>
                                )}
                                {runStatus === 'rejected' && (
                                    <div className="alert alert-warning py-2 small mt-3 mb-0">
                                        Payslips have <strong>NOT</strong> been released.
                                        Correct the issue and click <strong>Run Payroll</strong> to re-process.
                                    </div>
                                )}
                                {runStatus === 'pending_approval' && (
                                    <div className="alert alert-info py-2 small mt-3 mb-0">
                                        Payslips are <strong>waiting for finance approval</strong>.
                                        Go to <strong>Finance Approval</strong> tab.
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}
                    {(results.length > 0 || loadingHistory) && (
                        <Card className="border-0 shadow-sm animate-fade-in">
                            <Card.Header className="bg-white fw-bold py-3 border-0">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div>
                                        Payroll Records — {month}
                                        <Badge bg="secondary" className="ms-2">
                                            {filterSearch || filterDept ? `${filteredResults.length} / ` : ''}
                                            {results.length} employees
                                        </Badge>
                                    </div>
                                    {runStatus && (
                                        <Badge
                                            bg={
                                                runStatus === 'processed'        ? 'success' :
                                                runStatus === 'pending_approval' ? 'warning' :
                                                runStatus === 'rejected'         ? 'danger'  :
                                                runStatus === 'approved'         ? 'success' : 'secondary'
                                            }
                                            text={runStatus === 'pending_approval' ? 'dark' : 'white'}
                                            style={{ fontSize: 12 }}>
                                            Run: {runStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                        </Badge>
                                    )}
                                </div>
                                {runStatus === 'rejected' && (
                                    <div className="alert alert-danger py-2 small mt-2 mb-0">
                                        <strong>⚠ Rejected by Finance — Payslips NOT released</strong>
                                        {runDetails?.reject_reason && <div className="mt-1">Reason: {runDetails.reject_reason}</div>}
                                    </div>
                                )}
                                {runStatus === 'pending_approval' && (
                                    <div className="alert alert-warning py-2 small mt-2 mb-0">
                                        ⏳ Pending finance approval — payslips not yet released.
                                    </div>
                                )}
                                <div className="d-flex gap-2 flex-wrap mt-2">
                                    <Form.Control size="sm" placeholder="Search name or code…"
                                        style={{ maxWidth: 260 }} value={filterSearch}
                                        onChange={e => { setFilterSearch(e.target.value); setPageNum(1); }}
                                    />
                                    {filterSearch && (
                                        <Button size="sm" variant="outline-secondary"
                                            onClick={() => { setFilterSearch(''); setFilterDept(''); setPageNum(1); }}>
                                            Clear
                                        </Button>
                                    )}
                                    <span className="text-muted small align-self-center ms-auto">
                                        Page {pageNum} of {Math.max(1, Math.ceil(filteredResults.length / PAGE_SIZE))}
                                    </span>
                                </div>
                            </Card.Header>
                            {loadingHistory ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary"/>
                                    <p className="mt-2 text-muted small">Loading payroll records…</p>
                                </div>
                            ) : (
                                <Table hover responsive className="mb-0 align-middle small">
                                    <thead className="bg-light">
                                        <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                            <th className="ps-4">Employee</th>
                                            <th>Gross</th>
                                            <th>Deductions</th>
                                            <th>Net Salary</th>
                                            <th>Status</th>
                                            <th className="text-end pe-4">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedResults.map((rec, i) => {
                                            const empName = rec.first_name
                                                ? `${rec.first_name} ${rec.last_name}`
                                                : (rec.name || '—');
                                            const gross = rec.gross_salary || rec.gross || 0;
                                            const net   = rec.net_salary   || rec.net   || 0;
                                            const tds   = parseFloat(rec.tds_deduction || 0);
                                            const pf    = parseFloat(rec.pf_deduction  || 0);
                                            const pt    = parseFloat(rec.pt_deduction  || 0);
                                            const lop   = parseFloat(rec.lop_deduction || 0);
                                            const ded   = tds + pf + pt + lop;
                                            return (
                                                <tr key={rec.id || i}>
                                                    <td className="ps-4">
                                                        <div className="fw-bold">{empName}</div>
                                                        {rec.employee_code && (
                                                            <div className="text-muted" style={{ fontSize: 11 }}>
                                                                {rec.employee_code}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="fw-bold">{cur(gross)}</td>
                                                    <td className="text-danger">
                                                        <div className="fw-bold">-{cur(ded)}</div>
                                                        <div style={{ fontSize: 10 }}>
                                                            TDS {cur(tds)} + PF {cur(pf)} + PT {cur(pt)}
                                                            {lop > 0 ? ` + LOP ${cur(lop)}` : ''}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold text-success">{cur(net)}</span>
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            bg={
                                                                (rec.run_status || runStatus) === 'rejected'         ? 'danger'  :
                                                                (rec.run_status || runStatus) === 'pending_approval' ? 'warning' :
                                                                (rec.run_status || runStatus) === 'processed' ||
                                                                (rec.run_status || runStatus) === 'approved'         ? 'success' :
                                                                'secondary'
                                                            }
                                                            text={(rec.run_status || runStatus) === 'pending_approval' ? 'dark' : 'white'}
                                                            className="d-inline-flex align-items-center gap-1 px-2 py-1">
                                                            <CheckCircle size={11}/>
                                                            {(rec.run_status || runStatus) === 'rejected'         ? 'Rejected'         :
                                                             (rec.run_status || runStatus) === 'pending_approval' ? 'Pending Approval' :
                                                             (rec.run_status || runStatus) === 'processed' ||
                                                             (rec.run_status || runStatus) === 'approved'         ? 'Processed'       :
                                                             rec.status || 'Draft'}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end pe-4">
                                                        <Button variant="outline-primary" size="sm"
                                                            className="d-inline-flex align-items-center gap-1"
                                                            onClick={() => downloadPayslip(rec.id, empName)}>
                                                            <Download size={14}/>
                                                            <span className="d-none d-sm-inline">PDF</span>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                            {Math.ceil(filteredResults.length / PAGE_SIZE) > 1 && (
                                <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top flex-wrap gap-2">
                                    <span className="text-muted small">
                                        Showing {Math.min((pageNum - 1) * PAGE_SIZE + 1, filteredResults.length)}–
                                        {Math.min(pageNum * PAGE_SIZE, filteredResults.length)} of {filteredResults.length}
                                    </span>
                                    <div className="d-flex gap-1 flex-wrap">
                                        <Button size="sm" variant="outline-secondary"
                                            disabled={pageNum <= 1}
                                            onClick={() => setPageNum(p => p - 1)}>‹ Prev</Button>
                                        {[...Array(Math.min(5, Math.ceil(filteredResults.length / PAGE_SIZE)))].map((_, i) => {
                                            const pg = Math.max(1, Math.min(
                                                pageNum - 2,
                                                Math.ceil(filteredResults.length / PAGE_SIZE) - 4
                                            )) + i;
                                            return (
                                                <Button key={pg} size="sm"
                                                    variant={pg === pageNum ? 'primary' : 'outline-secondary'}
                                                    onClick={() => setPageNum(pg)}>
                                                    {pg}
                                                </Button>
                                            );
                                        })}
                                        <Button size="sm" variant="outline-secondary"
                                            disabled={pageNum >= Math.ceil(filteredResults.length / PAGE_SIZE)}
                                            onClick={() => setPageNum(p => p + 1)}>Next ›</Button>
                                    </div>
                                </div>
                            )}
                        </Card>
                    )}
                </>
            )}
            <ConfirmModal
                show={runModal}
                onHide={() => setRunModal(false)}
                onConfirm={handleRunPayroll}
                loading={processing}
                title={isReRunCurrentMonth ? '⚠ Re-run Already Released Payroll' : 'Run Payroll'}
                message={isReRunCurrentMonth
                    ? `Payroll for ${month} was already processed and released to employees. Re-running will recalculate and OVERWRITE existing payslips. Employees may have already viewed or downloaded them. Are you sure you want to continue?`
                    : `Process payroll for ${month}? This will calculate LOP, PF, TDS and PT for all active employees.`}
                confirmLabel={isReRunCurrentMonth ? 'Yes, Overwrite & Re-run' : 'Yes, Run Payroll'}
            />
            <ConfirmModal
                show={emailModal}
                onHide={() => setEmailModal(false)}
                onConfirm={handleBulkEmail}
                loading={emailing}
                title="Email All Payslips"
                message={`Send payslip emails to all ${results.length} employees for ${month}? Each employee will receive their payslip details by email.`}
                confirmLabel="Yes, Send All"
            />
        </div>
    );
};
export default PayrollManagement;
